import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  AllocationAnalysisResponse,
  AllocationAnalysisTreeNode,
  endpointAllocationAnalysisPOST,
} from '~/api/def/endpoints/portfolioAnalysisEndpoint';
import {
  latestSnapshotIdAtom,
  triggerHardRefetchAtom,
  aiReasoningMapAtom,
  portfolioIdAtom,
  portfolioCurrencyAtom,
  type AIReasoningEntry,
} from '~/atoms/dashboardAtom';
import { GET_AI_RECOMMENDATIONS } from '~/graphql/queries/portfolio';
import { GET_INSTRUMENTS_BY_TICKERS } from '~/graphql/queries/instruments';
import {
  SectorAllocationRow,
  SectorAllocationWidget,
} from './SectorAllocationWidget';

// Backend response type (actual structure returned from API)
type BackendRow = {
  id: string;
  name: string;
  isin?: string;
  ticker?: string;
  full_ticker?: string;
  sector?: string;
  row_type: string;
  metrics_by_scenario?: Record<
    string,
    {
      allocation_percent?: number;
      beta?: number;
      var_95?: number;
      cvar_95?: number;
      var_95_1d_impact?: number;
      cvar_95_1d_impact?: number;
      ytd_return?: number;
    }
  >;
  children?: BackendRow[];
};

type BackendResponse = {
  snapshot_id: string;
  portfolio_id?: string;
  included_scenarios: string[];
  rows: BackendRow[];
};

// Transform backend response to frontend format
function transformBackendResponse(
  response: BackendResponse,
): AllocationAnalysisResponse {
  const treeData: AllocationAnalysisTreeNode[] = [];

  // Recursive function to flatten nested children structure
  function flattenRows(rows: BackendRow[], parentPath: string[] = []) {
    for (const row of rows) {
      // Build orgHierarchy path
      const currentPath =
        row.row_type === 'total'
          ? ['Total']
          : row.row_type === 'sector'
            ? [...parentPath, row.name]
            : [...parentPath, row.name];

      // Transform metrics_by_scenario to scenarios
      const scenarios: Record<
        string,
        {
          percent?: number | null;
          beta?: number | null;
          var_95?: number | null;
          cvar_95?: number | null;
          var_95_1d_impact?: number | null;
          cvar_95_1d_impact?: number | null;
          ytd_return?: number | null;
        }
      > = {};
      if (row.metrics_by_scenario) {
        for (const [scenarioKey, metrics] of Object.entries(
          row.metrics_by_scenario,
        )) {
          scenarios[scenarioKey] = {
            percent: metrics.allocation_percent ?? null,
            beta: metrics.beta ?? null,
            var_95: metrics.var_95 ?? null,
            cvar_95: metrics.cvar_95 ?? null,
            var_95_1d_impact: metrics.var_95_1d_impact ?? null,
            cvar_95_1d_impact: metrics.cvar_95_1d_impact ?? null,
            ytd_return: metrics.ytd_return ?? null,
          };
        }
      }

      // Build the tree node
      const node: AllocationAnalysisTreeNode = {
        orgHierarchy: currentPath,
      };

      if (row.row_type === 'total') {
        node.sector = 'Total';
        node.scenarios = scenarios;
      } else if (row.row_type === 'sector') {
        node.sector = row.sector;
        node.scenarios = scenarios;
      } else if (row.row_type === 'holding') {
        node.isin = row.isin;
        node.company_name = row.name;
        const responseTicker = (row as BackendRow & { ticker?: string }).ticker;
        node.ticker = responseTicker || row.isin || row.name;
        node.full_ticker = row.isin || responseTicker || row.name;
        node.scenarios = scenarios;
      }

      treeData.push(node);

      // Process children
      if (row.children) {
        flattenRows(row.children, currentPath);
      }
    }
  }

  flattenRows(response.rows);

  return {
    snapshot_id: response.snapshot_id,
    included_scenarios: response.included_scenarios,
    tree_data: treeData,
  };
}

export type SectorAllocationWidgetWrapperProps = {
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  snapshotId?: string | null;
};

interface AIRecommendation {
  action: 'REDUCE' | 'INCREASE' | 'MAINTAIN';
  reason: string;
  ai_reasoning?: string;
  ticker: string;
  confidence: 'high' | 'medium' | 'low';
  current_allocation: number;
  recommended_allocation: number;
}

interface Instrument {
  id: string;
  ticker: string;
  full_ticker: string;
  name: string;
  isin: string;
  sector: string;
  currency: string;
  exchange: string;
  companyId: string;
}

interface AIAnalysisData {
  recommendations: AIRecommendation[];
}

interface AIRecommendationsData {
  EQPortfoliosAIAnalysis: {
    analysis_data: AIAnalysisData;
  }[];
}

export const SectorAllocationWidgetWrapper: React.FC<
  SectorAllocationWidgetWrapperProps
> = ({
  handleRemoveWidget,
  widgetId,
  title = 'Allocation Analysis',
  snapshotId: snapshotIdProp,
}) => {
  const snapshotIdFromAtom = useAtomValue(latestSnapshotIdAtom);
  const snapshotId = snapshotIdProp || snapshotIdFromAtom;
  const triggerHardRefetch = useAtomValue(triggerHardRefetchAtom);
  const setAIReasoningMap = useSetAtom(aiReasoningMapAtom);
  const portfolioId = useAtomValue(portfolioIdAtom);
  const portfolioCurrency = useAtomValue(portfolioCurrencyAtom);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [apiData, setApiData] = useState<AllocationAnalysisResponse | null>(
    null,
  );

  // Fetch AI Recommendations data for reasoning map
  const { data: aiRecommendationsData } = useQuery<AIRecommendationsData>(
    GET_AI_RECOMMENDATIONS,
    {
      variables: { snapshotId },
      notifyOnNetworkStatusChange: true,
      skip: !snapshotId,
    },
  );

  // Extract tickers from recommendations
  const tickers = useMemo(() => {
    if (
      !aiRecommendationsData?.EQPortfoliosAIAnalysis?.[0]?.analysis_data
        ?.recommendations
    )
      return [];
    return aiRecommendationsData.EQPortfoliosAIAnalysis[0].analysis_data.recommendations.map(
      (rec) => rec.ticker,
    );
  }, [aiRecommendationsData]);

  // Prepare variables for instruments query
  const instrumentVariables = useMemo(() => {
    return {
      tickers: tickers,
    };
  }, [tickers]);

  // Query instruments for all tickers
  const shouldSkipInstrumentQuery =
    tickers.length === 0 ||
    !tickers.some((ticker) => ticker && ticker.trim() !== '');

  const { data: instrumentsData } = useQuery(GET_INSTRUMENTS_BY_TICKERS, {
    variables: instrumentVariables,
    skip: shouldSkipInstrumentQuery,
    notifyOnNetworkStatusChange: true,
  });

  // Process AI Recommendations data and populate reasoning map
  useEffect(() => {
    if (
      !aiRecommendationsData?.EQPortfoliosAIAnalysis?.[0]?.analysis_data
        ?.recommendations
    ) {
      // Clear reasoning map if no data
      setAIReasoningMap({});
      return;
    }

    const analysisData =
      aiRecommendationsData.EQPortfoliosAIAnalysis[0].analysis_data;
    const instruments = instrumentsData?.EQInstrument || [];

    const recommendationsData = analysisData.recommendations.map(
      (rec: AIRecommendation) => {
        // Find matching instrument details
        const instrument = instruments.find((inst: Instrument) =>
          inst.full_ticker.startsWith(rec.ticker),
        );

        return {
          action: rec.action,
          ticker: rec.ticker,
          reason: rec.reason,
          ai_reasoning: rec.ai_reasoning,
          companyName: instrument?.name || null,
          instrumentTicker: instrument?.full_ticker || null,
        };
      },
    );

    const addEntryForKey = (
      acc: Partial<Record<string, AIReasoningEntry>>,
      key?: string | null,
      entry?: AIReasoningEntry,
    ) => {
      if (!key || !entry) return;
      const normalizedKey = key.toUpperCase();
      if (!normalizedKey) return;
      acc[normalizedKey] = entry;

      const alternativeKeys = new Set<string>();
      const splitDelimiters = [':', '.', ' '];
      splitDelimiters.forEach((delimiter) => {
        if (normalizedKey.includes(delimiter)) {
          const truncated = normalizedKey.split(delimiter)[0];
          if (truncated) {
            alternativeKeys.add(truncated);
          }
        }
      });

      const alphanumericOnly = normalizedKey.replace(/[^A-Z0-9]/g, '');
      if (alphanumericOnly) {
        alternativeKeys.add(alphanumericOnly);
      }

      alternativeKeys.forEach((altKey) => {
        if (altKey) {
          acc[altKey] = entry;
        }
      });
    };

    const reasoningMap = recommendationsData.reduce<
      Partial<Record<string, AIReasoningEntry>>
    >((acc, rec) => {
      const entry = {
        reason: rec.reason,
        aiReasoning: rec.ai_reasoning,
        companyName: rec.companyName,
      };
      addEntryForKey(acc, rec.ticker, entry);
      addEntryForKey(acc, rec.instrumentTicker, entry);
      return acc;
    }, {});

    setAIReasoningMap(reasoningMap);
  }, [aiRecommendationsData, instrumentsData, setAIReasoningMap]);

  // Fetch allocation analysis data
  const fetchData = useCallback(async () => {
    if (!snapshotId) {
      setApiData(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = (await endpointAllocationAnalysisPOST(snapshotId, {
        batch_types: ['current_holdings', 'proposed'],
      })) as unknown;

      // Backend returns a different structure - transform it
      if (response && typeof response === 'object' && 'rows' in response) {
        const transformedData = transformBackendResponse(
          response as BackendResponse,
        );
        setApiData(transformedData);
      } else {
        throw new Error('Invalid response format from allocation analysis API');
      }
    } catch (err) {
      console.error('Error fetching allocation analysis:', err);
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to fetch allocation data'),
      );
    } finally {
      setLoading(false);
    }
  }, [snapshotId]);

  // Fetch data when snapshotId changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Force hard refetch when triggered by other widgets
  useEffect(() => {
    if (triggerHardRefetch > 0) {
      fetchData();
    }
  }, [triggerHardRefetch, fetchData]);

  // Transform API data into tree structure for the widget
  const tableData = useMemo<SectorAllocationRow[]>(() => {
    if (!apiData?.tree_data) {
      return [];
    }

    try {
      return apiData.tree_data.map((node, index) => {
        // Validate orgHierarchy
        if (
          !Array.isArray(node.orgHierarchy) ||
          node.orgHierarchy.length === 0
        ) {
          console.error(`Invalid orgHierarchy at index ${index}:`, node);
          throw new Error(
            `Invalid orgHierarchy at row ${index}: must be a non-empty array`,
          );
        }

        // Extract current and proposed values from scenarios
        const currentPercent =
          node.scenarios?.current_holdings?.percent ?? null;
        const proposedPercent = node.scenarios?.proposed?.percent ?? null;

        // Extract additional metrics for current holdings
        const currentBeta = node.scenarios?.current_holdings?.beta ?? null;
        const currentVar951dImpact =
          node.scenarios?.current_holdings?.var_95_1d_impact ?? null;
        const currentCVar951dImpact =
          node.scenarios?.current_holdings?.cvar_95_1d_impact ?? null;
        const currentYtdReturn =
          node.scenarios?.current_holdings?.ytd_return ?? null;

        // Extract additional metrics for proposed holdings
        const proposedBeta = node.scenarios?.proposed?.beta ?? null;
        const proposedVar951dImpact =
          node.scenarios?.proposed?.var_95_1d_impact ?? null;
        const proposedCVar951dImpact =
          node.scenarios?.proposed?.cvar_95_1d_impact ?? null;
        const proposedYtdReturn = node.scenarios?.proposed?.ytd_return ?? null;

        const rowData = {
          orgHierarchy: node.orgHierarchy,
          sector: node.sector,
          sector_total: node.sector_total,
          ticker: node.ticker || node.full_ticker,
          full_ticker: node.full_ticker || node.ticker,
          isin: node.isin,
          company_name: node.company_name,
          current_holdings: currentPercent,
          proposed_holdings: proposedPercent,
          current_beta: currentBeta,
          current_var_95_1d_impact: currentVar951dImpact,
          current_cvar_95_1d_impact: currentCVar951dImpact,
          current_ytd_return: currentYtdReturn,
          proposed_beta: proposedBeta,
          proposed_var_95_1d_impact: proposedVar951dImpact,
          proposed_cvar_95_1d_impact: proposedCVar951dImpact,
          proposed_ytd_return: proposedYtdReturn,
        };

        return rowData;
      });
    } catch (err) {
      console.error('Error transforming allocation data:', err);
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to transform allocation data'),
      );
      return [];
    }
  }, [apiData]);

  return (
    <SectorAllocationWidget
      data={tableData}
      isLoading={loading}
      error={error}
      refetch={fetchData}
      handleRemoveWidget={handleRemoveWidget}
      widgetId={widgetId}
      title={title}
      snapshotId={snapshotId || undefined}
      currency={portfolioCurrency || 'EUR'}
    />
  );
};
